import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { JobCardInput, OperationQueryParams, RoutingOperationInput, routingService } from '../services/routing.service';
import masterDataService, { WorkCenter } from '../services/master-data.service';

export const useRoutingsPage = (params: { page?: number; limit?: number; search?: string }) =>
  useQuery({ queryKey: ['routings', 'page', params], queryFn: () => routingService.getPage(params), placeholderData: keepPreviousData });

export const useRoutingSummary = () => useQuery({ queryKey: ['routings', 'summary'], queryFn: () => routingService.getSummary() });

export const useRouting = (productId: string | undefined) =>
  useQuery({ queryKey: ['routings', 'one', productId], queryFn: () => routingService.getOne(productId as string), enabled: !!productId });

// Active work centers, for the pickers
export const useWorkCenterOptions = () =>
  useQuery({
    queryKey: ['workCenters', 'options'],
    queryFn: async (): Promise<WorkCenter[]> => {
      const result: unknown = await masterDataService.getWorkCenters({ limit: 100 });
      const rows = (Array.isArray(result) ? result : (result as { data?: WorkCenter[] })?.data ?? []) as WorkCenter[];
      return rows.filter((workCenter) => (workCenter as { isActive?: boolean }).isActive !== false);
    },
    staleTime: 60_000,
  });

export const useSaveRouting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, operations }: { productId: string; operations: RoutingOperationInput[] }) => routingService.save(productId, operations),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['routings'] }),
  });
};

export const useOperationsPage = (params: OperationQueryParams) =>
  useQuery({ queryKey: ['jobCards', 'page', params], queryFn: () => routingService.getOperations(params), placeholderData: keepPreviousData });

export const useOperationSummary = () => useQuery({ queryKey: ['jobCards', 'summary'], queryFn: () => routingService.getOperationSummary() });

export const useWorkOrderOperations = (workOrderId: number | undefined) =>
  useQuery({
    queryKey: ['jobCards', 'workOrder', workOrderId],
    queryFn: () => routingService.getWorkOrderOperations(workOrderId as number),
    enabled: workOrderId !== undefined,
  });

// A booking changes the operations and the cost of its work order
const useInvalidateJobCards = () => {
  const queryClient = useQueryClient();
  return () => {
    ['jobCards', 'workOrders', 'work-orders', 'manufacturing'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  };
};

export const useBookJobCard = () => {
  const invalidate = useInvalidateJobCards();
  return useMutation({
    mutationFn: ({ workOrderId, operationId, data }: { workOrderId: number; operationId: string; data: JobCardInput }) =>
      routingService.book(workOrderId, operationId, data),
    onSuccess: invalidate,
  });
};

export const useRemoveJobCard = () => {
  const invalidate = useInvalidateJobCards();
  return useMutation({
    mutationFn: ({ workOrderId, jobCardId }: { workOrderId: number; jobCardId: string }) => routingService.removeJobCard(workOrderId, jobCardId),
    onSuccess: invalidate,
  });
};
